import { ApiError, networkError, toApiError } from '@/lib/api/errors';
import type { Membership, SessionUser, SessionView, TokenPair } from '@/lib/api/types';

/*
 * Session storage and token refresh.
 *
 * - The access token (15 min) lives in memory, with a copy in this tab's
 *   sessionStorage so a reload does not need a refresh (the refresh endpoint
 *   is rate limited). It is scoped to the membership it was issued for.
 * - The refresh token, the user and the memberships are kept in localStorage
 *   so a reload or a second tab stays signed in. Trade-off: a script injected
 *   into the page (XSS) could read the refresh token. We keep that surface
 *   small (no third-party scripts, React escaping, no inline HTML) and the
 *   backend rotates refresh tokens and revokes the whole family on reuse. An
 *   httpOnly cookie would be stronger but needs backend support (see
 *   BACKEND_REQUESTS.md).
 * - Refresh tokens rotate on every use and a reused one revokes the family,
 *   so a refresh token must never be sent twice: refreshes are single-flight
 *   inside the tab and serialized across tabs with the Web Locks API, always
 *   reading the newest token from storage first.
 */

const STORAGE_KEY = 'tabashir.session.v1';
/** This tab's access token, so a reload does not need a refresh round-trip. */
const ACCESS_KEY = 'tabashir.access.v1';
const LOCK_NAME = 'tabashir:refresh';
/** Refresh this long before the access token expires. */
const EXPIRY_SKEW_MS = 30_000;

export interface PersistedSession {
  refreshToken: string;
  user: SessionUser;
  memberships: Membership[];
  currentMembershipId: string | null;
}

export interface SessionState extends PersistedSession {
  accessToken: string | null;
  accessExpiresAt: number;
}

type Listener = () => void;

function readStorage(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    return typeof parsed?.refreshToken === 'string' && parsed.user ? parsed : null;
  } catch {
    return null;
  }
}

function writeStorage(s: PersistedSession | null): void {
  try {
    if (s) {
      const { refreshToken, user, memberships, currentMembershipId } = s;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ refreshToken, user, memberships, currentMembershipId }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked (private mode): the session lasts for this tab only.
  }
}

interface CachedAccess {
  token: string;
  expiresAt: number;
  membershipId: string | null;
}

function readAccess(membershipId: string | null): { accessToken: string | null; accessExpiresAt: number } {
  try {
    const cached = JSON.parse(sessionStorage.getItem(ACCESS_KEY) ?? 'null') as CachedAccess | null;
    if (cached && cached.membershipId === membershipId && cached.expiresAt - Date.now() > EXPIRY_SKEW_MS) {
      return { accessToken: cached.token, accessExpiresAt: cached.expiresAt };
    }
  } catch {
    // ignore
  }
  return { accessToken: null, accessExpiresAt: 0 };
}

function writeAccess(s: SessionState | null): void {
  try {
    if (s?.accessToken) {
      const cached: CachedAccess = { token: s.accessToken, expiresAt: s.accessExpiresAt, membershipId: s.currentMembershipId };
      sessionStorage.setItem(ACCESS_KEY, JSON.stringify(cached));
    } else sessionStorage.removeItem(ACCESS_KEY);
  } catch {
    // ignore
  }
}

let state: SessionState | null = (() => {
  const stored = readStorage();
  return stored ? { ...stored, ...readAccess(stored.currentMembershipId) } : null;
})();
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

function set(next: SessionState | null, persist = true): void {
  state = next;
  if (persist) writeStorage(next);
  writeAccess(next);
  emit();
}

export const session = {
  get: (): SessionState | null => state,

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** After login or setting a password. */
  start(view: SessionView): void {
    set({
      refreshToken: view.tokens.refreshToken,
      accessToken: view.tokens.accessToken,
      accessExpiresAt: Date.now() + view.tokens.expiresIn * 1000,
      user: view.user,
      memberships: view.memberships,
      currentMembershipId: view.currentMembershipId,
    });
  },

  applyTokens(pair: TokenPair, patch: Partial<PersistedSession> = {}): void {
    if (!state) return;
    set({
      ...state,
      ...patch,
      refreshToken: pair.refreshToken,
      accessToken: pair.accessToken,
      accessExpiresAt: Date.now() + pair.expiresIn * 1000,
    });
  },

  update(patch: Partial<PersistedSession>): void {
    if (state) set({ ...state, ...patch });
  },

  clear(): void {
    if (state) set(null);
  },
};

// Keep tabs in sync: sign out everywhere, and reload a tab whose school was
// switched in another tab (its refresh token now belongs to the new school).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    const stored = readStorage();
    if (!stored) {
      set(null, false);
      return;
    }
    if (state && stored.currentMembershipId !== state.currentMembershipId) {
      window.location.reload();
      return;
    }
    if (state) set({ ...state, refreshToken: stored.refreshToken }, false);
  });
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? locks.request(LOCK_NAME, fn) : fn();
}

/** Sends the newest refresh token to an auth endpoint and stores the rotated pair. */
async function rotate(
  path: '/api/auth/refresh' | '/api/auth/switch',
  extra: Record<string, unknown>,
  patch: Partial<PersistedSession> = {},
): Promise<TokenPair> {
  return withRefreshLock(async () => {
    const refreshToken = state ? (readStorage()?.refreshToken ?? state.refreshToken) : null;
    if (!refreshToken) throw new ApiError(401, 'TOKEN_INVALID', 'Not signed in');
    let res: Response;
    try {
      res = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken, ...extra }),
      });
    } catch (e) {
      throw networkError(e);
    }
    const body = await readJson(res);
    if (!res.ok) {
      const error = toApiError(res.status, body);
      if (res.status === 401) session.clear();
      throw error;
    }
    const pair = body as TokenPair;
    // Persist before releasing the lock so the next tab reads the new token.
    session.applyTokens(pair, patch);
    return pair;
  });
}

let inflight: Promise<string> | null = null;

/** Single-flight refresh: concurrent callers share one request. */
export function refreshAccessToken(): Promise<string> {
  inflight ??= rotate('/api/auth/refresh', {})
    .then((pair) => pair.accessToken)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** A usable access token, refreshing first when it is missing or about to expire. */
export async function validAccessToken(): Promise<string> {
  const s = state;
  if (!s) throw new ApiError(401, 'UNAUTHENTICATED', 'Not signed in');
  if (s.accessToken && s.accessExpiresAt - Date.now() > EXPIRY_SKEW_MS) return s.accessToken;
  return refreshAccessToken();
}

/** Re-scopes the session to another membership (null = unscoped, for the platform admin). */
export async function switchMembership(membershipId: string | null): Promise<void> {
  if (inflight) await inflight.catch(() => undefined);
  await rotate('/api/auth/switch', { membershipId }, { currentMembershipId: membershipId });
}

/** Revokes the refresh token (best effort) and forgets the session. */
export async function signOut(): Promise<void> {
  const refreshToken = state?.refreshToken;
  session.clear();
  if (!refreshToken) return;
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      keepalive: true,
    });
  } catch {
    // Offline: the token expires on its own.
  }
}

/** Test hook. */
export function resetSessionForTests(next: SessionState | null = null): void {
  inflight = null;
  state = next;
  writeStorage(next);
}
